/**
 * SPEC §11 – urenrapport (fase 2) en weekstaten goedkeuren (fase 3).
 */
import { useQuery } from '@tanstack/react-query';
import { Icoon } from '../ui/Icoon';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDagen, fmtDag, fmtDuur, isoWeek, maandagVan, vandaag } from '@ep/core';
import { api } from '../lib/api';
import { useActie, useData, useMag } from '../lib/data';
import { Badge, Laden } from '../ui/ui';
import { useVerversUren } from '../ui/Uren';

interface RapportRegel { id: string; datum: string; minuten: number; omschrijving: string | null; facturabel: boolean; bron: string; goedgekeurd: boolean; lopend: boolean; engineerId: string; engineer: string; soort: string | null; doel: string; sub: string; projectId: string | null; projectNummer: string | null; projectNaam: string | null }

const csvVeld = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;

export function UrenRapport() {
  const { data: d } = useData();
  const begin = `${vandaag().slice(0, 7)}-01`;
  const [f, setF] = useState({ van: begin, tot: vandaag(), projectId: '', engineerId: '' });
  const [groep, setGroep] = useState<'project' | 'engineer' | 'week'>('project');
  const q = useQuery({ queryKey: ['rapport', f], queryFn: () => api.get<{ regels: RapportRegel[] }>(`/api/tijd/rapport?van=${f.van}&tot=${f.tot}${f.projectId ? `&projectId=${f.projectId}` : ''}${f.engineerId ? `&engineerId=${f.engineerId}` : ''}`) });
  const regels = q.data?.regels ?? [];
  const groepen = useMemo(() => {
    const m = new Map<string, { titel: string; sub: Map<string, { titel: string; min: number; fact: number }>; min: number; fact: number }>();
    for (const r of regels) {
      const [k, t] = groep === 'project' ? [r.projectId ?? '-', `${r.projectNummer ?? ''} ${r.projectNaam ?? 'zonder project'}`] : groep === 'engineer' ? [r.engineerId, r.engineer] : [maandagVan(r.datum), `Week ${isoWeek(r.datum)} (${fmtDag(maandagVan(r.datum))})`];
      const [sk, st] = groep === 'project' ? [`${r.soort}:${r.doel}`, `${r.doel}${r.sub && r.soort === 'ITEM' ? ` · ${r.sub}` : ''}`] : [`${r.projectId}:${r.doel}`, `${r.projectNummer ?? ''} · ${r.doel}`];
      const g = m.get(k) ?? { titel: t, sub: new Map(), min: 0, fact: 0 };
      const s = g.sub.get(sk) ?? { titel: st, min: 0, fact: 0 };
      s.min += r.minuten;
      g.min += r.minuten;
      if (r.facturabel) { s.fact += r.minuten; g.fact += r.minuten; }
      g.sub.set(sk, s);
      m.set(k, g);
    }
    return [...m.values()].sort((a, b) => b.min - a.min);
  }, [regels, groep]);
  const totaal = regels.reduce((t, r) => t + r.minuten, 0);
  const fact = regels.filter((r) => r.facturabel).reduce((t, r) => t + r.minuten, 0);
  const exporteer = () => {
    const kop = ['Datum', 'Engineer', 'Projectnummer', 'Project', 'Soort', 'Waaraan', 'Werkpakket/taak', 'Omschrijving', 'Uren', 'Facturabel', 'Bron', 'Goedgekeurd'];
    const rijen = regels.map((r) => [r.datum, r.engineer, r.projectNummer, r.projectNaam, r.soort, r.doel, r.sub, r.omschrijving, (r.minuten / 60).toFixed(2).replace('.', ','), r.facturabel ? 'ja' : 'nee', r.bron, r.goedgekeurd ? 'ja' : 'nee']);
    const csv = '﻿' + [kop, ...rijen].map((x) => x.map(csvVeld).join(';')).join('\r\n') + '\r\n\r\nGemaakt met bsmartr – app.bsmartr.nl';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `uren-${f.van}-tm-${f.tot}.csv`;
    a.click();
  };
  if (!d) return <Laden />;
  return (
    <div className="pagina kolom" style={{ gap: 16 }}>
      <div className="paneel kolom" style={{ gap: 12 }}>
        <div className="rij wrap">
          <h1 className="groei">Urenrapport</h1>
          <button className="knop" disabled={!regels.length} onClick={exporteer}><Icoon naam="download" maat={16} /> Exporteren naar Excel</button>
        </div>
        <div className="rij wrap">
          <label className="klein">Van <input type="date" value={f.van} onChange={(e) => setF({ ...f, van: e.target.value })} style={{ width: 'auto' }} /></label>
          <label className="klein">t/m <input type="date" value={f.tot} onChange={(e) => setF({ ...f, tot: e.target.value })} style={{ width: 'auto' }} /></label>
          <select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value })} style={{ width: 'auto' }}>
            <option value="">Alle projecten</option>
            {d.projecten.map((p) => <option key={p.id} value={p.id}>{p.nummer} {p.naam}</option>)}
          </select>
          <select value={f.engineerId} onChange={(e) => setF({ ...f, engineerId: e.target.value })} style={{ width: 'auto' }}>
            <option value="">Alle engineers</option>
            {d.engineers.map((e) => <option key={e.id} value={e.id}>{e.naam}</option>)}
          </select>
          <select value={groep} onChange={(e) => setGroep(e.target.value as 'project')} style={{ width: 'auto' }}>
            <option value="project">Per project</option>
            <option value="engineer">Per engineer</option>
            <option value="week">Per week</option>
          </select>
        </div>
        <div className="kaartjes">
          <div className="kaartje"><div className="getal">{fmtDuur(totaal)}</div><div className="lbl">uren geschreven</div></div>
          <div className="kaartje"><div className="getal">{fmtDuur(fact)}</div><div className="lbl">facturabel ({totaal ? Math.round((fact / totaal) * 100) : 0}%)</div></div>
          <div className="kaartje"><div className="getal">{new Set(regels.map((r) => r.engineerId)).size}</div><div className="lbl">engineers</div></div>
        </div>
        {!q.data ? <Laden /> : (
          <table className="tabel">
            <thead><tr><th>Waaraan</th><th className="rechts">Uren</th><th className="rechts">Facturabel</th></tr></thead>
            <tbody>
              {groepen.map((g) => (
                <Fragment key={g.titel}>
                  <tr style={{ background: 'var(--accent-bg)' }}><td className="vet">{g.titel}</td><td className="rechts vet">{fmtDuur(g.min)}</td><td className="rechts vet">{fmtDuur(g.fact)}</td></tr>
                  {[...g.sub.values()].sort((a, b) => b.min - a.min).map((s) => (
                    <tr key={g.titel + s.titel}><td style={{ paddingLeft: 24 }}>{s.titel}</td><td className="rechts">{fmtDuur(s.min)}</td><td className="rechts">{fmtDuur(s.fact)}</td></tr>
                  ))}
                </Fragment>
              ))}
              {!groepen.length && <tr><td colSpan={3} className="leeg">Geen uren in deze periode.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface Rij { id: string; naam: string; minuten: number; facturabel: number; beschikbaar: number; regels: number; lopend: boolean; status: 'leeg' | 'open' | 'goedgekeurd'; goedgekeurdDoor: string | null }

export function UrenGoedkeuren() {
  const mag = useMag();
  const ververs = useVerversUren();
  const [maandag, setMaandag] = useState(maandagVan(addDagen(vandaag(), -7)));
  const q = useQuery({ queryKey: ['goedkeuren', maandag], queryFn: () => api.get<{ maandag: string; engineers: Rij[] }>(`/api/tijd/goedkeuren?maandag=${maandag}`) });
  const zet = useActie((a: { engineerId: string; goed: boolean }) => api.post('/api/tijd/goedkeuren', { ...a, maandag }), { succes: 'Bijgewerkt', opNa: () => { ververs(); void q.refetch(); } });
  if (!mag('uren.goedkeuren')) return <div className="pagina"><div className="paneel leeg">Alleen projectmanagers en beheerders keuren uren goed.</div></div>;
  const open = q.data?.engineers.filter((e) => e.status === 'open') ?? [];
  return (
    <div className="pagina kolom" style={{ gap: 16 }}>
      <div className="paneel kolom" style={{ gap: 12 }}>
        <div className="rij wrap">
          <h1 className="groei">Uren goedkeuren</h1>
          <button className="knop" onClick={() => setMaandag(addDagen(maandag, -7))}>‹</button>
          <b>Week {isoWeek(maandag)} · {fmtDag(maandag)} – {fmtDag(addDagen(maandag, 4))}</b>
          <button className="knop" onClick={() => setMaandag(addDagen(maandag, 7))}>›</button>
          {open.length > 0 && <button className="knop merk" onClick={() => open.filter((e) => !e.lopend).forEach((e) => zet.mutate({ engineerId: e.id, goed: true }))}>Alles goedkeuren ({open.length})</button>}
        </div>
        <p className="klein mute" style={{ margin: 0 }}>Na goedkeuring ligt de week vast voor de engineer. Heropenen kan altijd.</p>
        {!q.data ? <Laden /> : (
          <table className="tabel">
            <thead><tr><th>Engineer</th><th className="rechts">Geschreven</th><th className="rechts">Beschikbaar</th><th className="rechts">Facturabel</th><th>Status</th><th /></tr></thead>
            <tbody>
              {q.data.engineers.map((e) => (
                <tr key={e.id}>
                  <td><Link to={`/mijn-uren?engineer=${e.id}&week=${maandag}`}>{e.naam}</Link></td>
                  <td className="rechts" style={{ color: e.beschikbaar && e.minuten < e.beschikbaar * 60 * 0.9 ? 'var(--oranje)' : undefined }}>{fmtDuur(e.minuten)}</td>
                  <td className="rechts">{fmtDuur(e.beschikbaar * 60)}</td>
                  <td className="rechts">{fmtDuur(e.facturabel)}</td>
                  <td>{e.status === 'goedgekeurd' ? <Badge toon="groen">goedgekeurd{e.goedgekeurdDoor ? ` · ${e.goedgekeurdDoor}` : ''}</Badge> : e.status === 'open' ? <Badge toon="oranje">{e.lopend ? 'timer loopt' : 'te keuren'}</Badge> : <Badge toon="grijs">geen uren</Badge>}</td>
                  <td className="rechts">
                    {e.status === 'open' && <button className="knop primair klein" disabled={e.lopend} onClick={() => zet.mutate({ engineerId: e.id, goed: true })}>Goedkeuren</button>}
                    {e.status === 'goedgekeurd' && <button className="knop klein" onClick={() => zet.mutate({ engineerId: e.id, goed: false })}>Heropenen</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
